import React, { useState, useEffect } from 'react';
import styles from './filters.module.css';
import { Filter as FilterProp } from '../../typescript/component';
import { translate } from '../../helper/translate';
import SidebarModal from '../ui/sidebar-modal';
import { useRouter } from 'next/router';

type Props = {
  filter: FilterProp;
  onApplyFilters: (selectedFilters: {
    locations: string[];
    certifications: string[];
    metals: string[];
  }) => void;
  defaultSelected?: {
    locations: string[];
    certifications: string[];
    metals: string[];
  };
};

export default function Filter({ filter, onApplyFilters, defaultSelected }: Props) {
  const [isExpanded, setIsExpanded] = useState<string | null>(null);
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
  const [selectedCertifications, setSelectedCertifications] = useState<string[]>([]);
  const [selectedMetals, setSelectedMetals] = useState<string[]>([]);
  const { locale } = useRouter();

  // Apply default selected filters on mount
  useEffect(() => {
    if (defaultSelected) {
      setSelectedLocations(defaultSelected.locations || []);
      setSelectedCertifications(defaultSelected.certifications || []);
      setSelectedMetals(defaultSelected.metals || []);
      // Don't call onApplyFilters here to avoid URL conflicts
    }
  }, [defaultSelected]);

  // Auto-expand continents that have selected sub-locations
  useEffect(() => {
    if (selectedLocations.length > 0 && filter?.locations) {
      const continentsWithSelectedChildren = filter.locations
        .filter(loc => loc.depth === 1)
        .filter(continent => {
          const subLocations = getSubLocations(continent.uid);
          return subLocations.some(subLoc => selectedLocations.includes(subLoc.uid));
        })
        .map(continent => continent.uid);

      if (continentsWithSelectedChildren.length > 0) {
        setIsExpanded(continentsWithSelectedChildren[0]); // Expand the first one with selected children
      }
    }
  }, [selectedLocations, filter?.locations]);

  const applyFilters = (
    locations = selectedLocations,
    certifications = selectedCertifications,
    metals = selectedMetals,
  ) => {
    onApplyFilters({
      locations,
      certifications,
      metals,
    });
  };

  const toggleExpand = (uid: string) => {
    setIsExpanded(isExpanded === uid ? null : uid);
  };

  const handleLocationChange = (uid: string) => {
    const updated = selectedLocations.includes(uid)
      ? selectedLocations.filter(id => id !== uid)
      : [...selectedLocations, uid];
    setSelectedLocations(updated);
    applyFilters(updated, selectedCertifications, selectedMetals);
  };

  const handleCertificationChange = (uid: string) => {
    const updated = selectedCertifications.includes(uid)
      ? selectedCertifications.filter(id => id !== uid)
      : [...selectedCertifications, uid];
    setSelectedCertifications(updated);
    applyFilters(selectedLocations, updated, selectedMetals);
  };

  const handleMetalsChange = (uid: string) => {
    const updated = selectedMetals.includes(uid)
      ? selectedMetals.filter(id => id !== uid)
      : [...selectedMetals, uid];
    setSelectedMetals(updated);
    applyFilters(selectedLocations, selectedCertifications, updated);
  };

  const clearFilters = () => {
    setSelectedLocations([]);
    setSelectedCertifications([]);
    setSelectedMetals([]);
    onApplyFilters({ locations: [], certifications: [], metals: [] });
  };

  const getSubLocations = (parentUid: string) =>
    filter?.locations?.filter(loc => loc.parent_uid === parentUid) || [];

  return (
    <SidebarModal
      content={
        <div className={styles.aboutBanner}>
          <div className={styles.root}>
            <section className={styles.locationsOptions}>
              <div className={styles.categoryOptions}>
                <div className={styles.category}>
                  <a className={styles.filters}>{translate('filters', locale || 'en-us')}</a>
                </div>
                <div className={styles.clearAllFiltersButton}>
                  <a className={styles.clearAllFilters} onClick={clearFilters}>
                    {translate('clear_all_filters', locale || 'en-us')}
                  </a>
                </div>
              </div>

              <div className={styles.filterOptions}>
                <div className={styles.locationFilter}>
                  {/* LOCATIONS */}
                  {filter?.locations && filter.locations.length > 0 && (
                    <div className={styles.locationOptions}>
                      <div className={styles.locationName}>
                        <div className={styles.filterHeader}>
                          {translate('locations', locale || 'en-us')}
                        </div>
                      </div>
                      <div className={styles.locationSelect}>
                        <div className={styles.locationsSelectWrapper}>
                          {filter.locations
                            .filter(loc => loc.depth === 1)
                            .map(location => (
                              <div key={location.uid}>
                                <label className={styles.locationSelectOptions}>
                                  <input
                                    type="checkbox"
                                    value={location.uid}
                                    onChange={() => toggleExpand(location.uid)}
                                  />
                                  <span
                                    className={`${styles.selectBoxExpand} ${
                                      isExpanded === location.uid ? styles.minus : styles.plus
                                    }`}
                                    onClick={() => toggleExpand(location.uid)}
                                  ></span>
                                  <span className={styles.continent}>{translate(location.name.toLowerCase(), locale || 'en-us', location.name)}</span>
                                </label>
                                {isExpanded === location.uid && (
                                  <div className={styles.subLocationWrapper}>
                                    {getSubLocations(location.uid).map(subLocation => (
                                      <label
                                        key={subLocation.uid}
                                        className={styles.locationSelectOptionsIndent}
                                      >
                                        <input
                                          type="checkbox"
                                          value={subLocation.uid}
                                          onChange={() => handleLocationChange(subLocation.uid)}
                                          checked={selectedLocations.includes(subLocation.uid)}
                                        />
                                        <span className={styles.selectBoxCheck}></span>
                                        <span className={styles.country}>{translate(subLocation.name.toLowerCase(), locale || 'en-us', subLocation.name)}</span>
                                      </label>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* CERTIFICATIONS */}
                  {filter?.certifications && filter.certifications.length > 0 && (
                    <div className={styles.certificationSelect}>
                      <div className={styles.certifications}>
                        <div className={styles.filterHeader}>
                          {translate('certifications', locale || 'en-us')}
                        </div>
                      </div>
                      {filter.certifications.map(cert => (
                        <label key={cert.uid} className={styles.locationSelectOptions}>
                          <input
                            type="checkbox"
                            value={cert.uid}
                            onChange={() => handleCertificationChange(cert.uid)}
                            checked={selectedCertifications.includes(cert.uid)}
                          />
                          <span className={styles.selectBoxCheck}></span>
                          <span className={styles.country}>{cert.name}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </section>
          </div>
        </div>
      }
    />
  );
}
